import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@primitives/dialog";
import { ScrollArea } from "@primitives/scroll-area";
import { fetchCurrentTerms } from "@/server/services/termsService";
import { QUERY_KEYS } from "@/config/queryKeys";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function TermsModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation();

  const {
    data: terms,
    isLoading,
    isError,
  } = useQuery({
    queryKey: QUERY_KEYS.termsCurrent,
    queryFn: fetchCurrentTerms,
    enabled: open,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
          <DialogTitle className="text-base font-semibold leading-snug">
            {t("terms.title")}
          </DialogTitle>
          <DialogDescription className="mt-0.5 text-xs text-muted-foreground">
            {t("terms.issuer")}
            {terms && (
              <>
                <br />
                {t("terms.effectiveDate", {
                  date: new Date(terms.effective_date).toLocaleDateString(),
                })}
                <br />
                {t("terms.version", { version: terms.version })}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="px-6 py-5">
            {isLoading && (
              <p className="py-10 text-center text-sm text-muted-foreground">
                {t("terms.loading")}
              </p>
            )}

            {isError && (
              <p className="py-10 text-center text-sm text-destructive">
                {t("terms.error")}
              </p>
            )}

            {terms && (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {terms.content}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
